// src/components/quiz/SectionIntro.tsx
import { motion } from 'framer-motion'
import type { Section } from '../../hooks/useQuizState'

interface SectionIntroProps {
  section: Section
  title: string
  body: string
  onContinue: () => void
  isWarmSection?: boolean
}

const sectionColors: Record<Section, string> = {
  A: 'var(--color-teal)',
  B: 'var(--color-deep-teal)',
  C: 'var(--color-rose)',
  D: 'var(--color-deep-teal)',
  E: 'var(--color-teal)',
}

export function SectionIntro({ section, title, body, onContinue, isWarmSection }: SectionIntroProps) {
  const accentColor = sectionColors[section]

  return (
    <div style={{
      minHeight: '100dvh',
      background: isWarmSection ? 'var(--color-warm-bg)' : 'var(--color-cream)',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '2rem 1.5rem',
      transition: 'background 0.6s ease',
    }}>
      <div style={{ width: '100%', maxWidth: '560px' }}>
        {/* Section badge */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          style={{ marginBottom: '1.5rem' }}
        >
          <span style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.5rem',
            padding: '0.35rem 0.875rem',
            background: `${accentColor}15`,
            borderRadius: 'var(--radius-full)',
            fontSize: '0.75rem',
            fontWeight: 600,
            color: accentColor,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
          }}>
            <span style={{
              width: '6px', height: '6px',
              borderRadius: '50%',
              background: accentColor,
              display: 'inline-block',
            }} />
            Section {section}
          </span>
        </motion.div>

        {/* Title */}
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
          style={{
            fontFamily: 'var(--font-serif)',
            fontSize: 'clamp(1.75rem, 5vw, 2.5rem)',
            color: 'var(--color-deep-teal)',
            lineHeight: 1.2,
            marginBottom: '1.25rem',
          }}
        >
          {title}
        </motion.h2>

        {/* Body */}
        <motion.p
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.25, ease: [0.22, 1, 0.36, 1] }}
          style={{
            fontSize: '1rem',
            color: 'var(--color-slate)',
            lineHeight: 1.75,
            marginBottom: '2.5rem',
          }}
        >
          {body}
        </motion.p>

        {/* CTA */}
        <motion.button
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
          whileHover={{ scale: 1.03 }}
          whileTap={{ scale: 0.97 }}
          onClick={onContinue}
          style={{
            padding: '0.875rem 2rem',
            background: accentColor,
            color: 'white',
            border: 'none',
            borderRadius: 'var(--radius-xl)',
            fontSize: '0.95rem',
            fontWeight: 600,
            cursor: 'pointer',
            fontFamily: 'var(--font-sans)',
            letterSpacing: '0.01em',
          }}
        >
          Continue →
        </motion.button>
      </div>
    </div>
  )
}
